import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router";
import { useSession } from "@/app/session";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Shell } from "@/components/Shell";
import { Alert, Button, Field, SectionTitle } from "@/components/ui";
import { api } from "@/lib/api";
import { authClient } from "@/lib/authClient";
import { errText, useT } from "@/lib/i18n";
import { currentSubscription, disablePush, enablePush, pushSupported } from "@/lib/push";

/** Account and workspace settings: name, password, language, workspace name, sessions. */
export default function Settings() {
  const t = useT();
  const { me, refresh } = useSession();
  const [name, setName] = useState(me?.user.name ?? "");
  const [wsName, setWsName] = useState(me?.workspace.name ?? "");
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState<{ kind: "info" | "error" | "success"; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pushOn, setPushOn] = useState<boolean | null>(null);
  useEffect(() => {
    currentSubscription()
      .then((s) => setPushOn(!!s))
      .catch(() => setPushOn(false));
  }, []);
  if (!me) return null;

  const run = async (key: string, fn: () => Promise<void>, ok: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      setMsg({ kind: "success", text: ok });
    } catch (e) {
      setMsg({ kind: "error", text: errText(t, (e as { code?: string }).code ?? (e as Error).message) });
    } finally {
      setBusy(null);
    }
  };

  async function saveName(e: FormEvent) {
    e.preventDefault();
    await run(
      "name",
      async () => {
        const r = await authClient.updateUser({ name: name.trim() });
        if (r.error) throw new Error(r.error.code ?? r.error.message);
        await refresh();
      },
      t.saved,
    );
  }
  async function savePassword(e: FormEvent) {
    e.preventDefault();
    if (pw.length < 8) return setMsg({ kind: "error", text: t.passwordTooShort });
    if (pw !== pw2) return setMsg({ kind: "error", text: t.passwordMismatch });
    await run(
      "pw",
      async () => {
        const r = await authClient.changePassword({
          currentPassword: cur,
          newPassword: pw,
          revokeOtherSessions: true,
        });
        if (r.error) throw new Error(r.error.code ?? r.error.message);
        setCur("");
        setPw("");
        setPw2("");
      },
      t.passwordUpdated,
    );
  }
  async function saveWorkspace(e: FormEvent) {
    e.preventDefault();
    await run(
      "ws",
      async () => {
        await api("/api/workspace", { method: "PATCH", json: { name: wsName.trim() } });
        await refresh();
      },
      t.saved,
    );
  }
  async function signOutEverywhere() {
    await run(
      "sessions",
      async () => {
        const r = await authClient.revokeOtherSessions();
        if (r.error) throw new Error(r.error.code ?? r.error.message);
      },
      t.sessionsRevoked,
    );
  }

  return (
    <Shell>
      <div className="mx-auto max-w-2xl px-6 py-8">
        <h1 className="display mb-6 text-4xl">.{t.settings}</h1>
        {msg ? (
          <div className="mb-4">
            <Alert kind={msg.kind}>{msg.text}</Alert>
          </div>
        ) : null}

        <section className="mb-8">
          <SectionTitle>{t.profile}</SectionTitle>
          <form onSubmit={saveName} className="flex flex-col gap-3">
            <Field
              label={t.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={120}
            />
            <Field label={t.email} value={me.user.email} readOnly disabled />
            <div>
              <Button type="submit" loading={busy === "name"}>
                {t.save}
              </Button>
            </div>
          </form>
        </section>

        <section className="mb-8">
          <SectionTitle>{t.language}</SectionTitle>
          <p className="mb-2 text-sm text-muted">{t.languageHint}</p>
          <LanguageToggle />
        </section>

        <section className="mb-8">
          <SectionTitle>{t.password}</SectionTitle>
          <p className="mb-3 text-sm text-muted">{t.passwordHint}</p>
          <form onSubmit={savePassword} className="flex flex-col gap-3">
            <Field
              label={t.currentPassword}
              type="password"
              autoComplete="current-password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
            />
            <Field
              label={t.newPassword}
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              minLength={8}
            />
            <Field
              label={t.confirmPassword}
              type="password"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={8}
            />
            <div className="flex items-center gap-3">
              <Button type="submit" loading={busy === "pw"}>
                {t.changePassword}
              </Button>
              <a href="/recuperar" className="text-sm text-muted hover:text-text">
                {t.noPasswordYet}
              </a>
            </div>
          </form>
        </section>

        {me.staffRole === "owner" || me.staffRole === "admin" ? (
          <section className="mb-8">
            <SectionTitle>{t.workspace}</SectionTitle>
            <form onSubmit={saveWorkspace} className="flex flex-col gap-3">
              <Field
                label={t.workspaceName}
                value={wsName}
                onChange={(e) => setWsName(e.target.value)}
                required
                maxLength={255}
              />
              <div>
                <Button type="submit" loading={busy === "ws"}>
                  {t.save}
                </Button>
              </div>
            </form>
          </section>
        ) : null}

        {me.staffRole === "owner" || me.staffRole === "admin" ? (
          <section className="mb-8">
            <SectionTitle>{t.usage.settingTitle}</SectionTitle>
            <p className="mb-3 text-sm text-muted">{t.usage.settingHint}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={me.workspace.usageTracking ? "ghost" : "primary"}
                loading={busy === "usage"}
                onClick={() =>
                  run(
                    "usage",
                    async () => {
                      await api("/api/workspace", {
                        method: "PATCH",
                        json: { usageTracking: !me.workspace.usageTracking },
                      });
                      await refresh();
                    },
                    t.saved,
                  )
                }
              >
                {me.workspace.usageTracking ? t.usage.settingOn : t.usage.settingOff}
              </Button>
              <Link to="/uso" className="text-sm text-muted hover:text-text">
                {t.usage.view}
              </Link>
            </div>
          </section>
        ) : null}

        {me.staffRole === "owner" || me.staffRole === "admin" ? (
          <section className="mb-8">
            <SectionTitle>{t.feedback.settingTitle}</SectionTitle>
            <p className="mb-3 text-sm text-muted">{t.feedback.settingHint}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={me.workspace.feedbackEnabled ? "ghost" : "primary"}
                loading={busy === "feedback"}
                onClick={() =>
                  run(
                    "feedback",
                    async () => {
                      await api("/api/workspace", {
                        method: "PATCH",
                        json: { feedbackEnabled: !me.workspace.feedbackEnabled },
                      });
                      await refresh();
                    },
                    t.saved,
                  )
                }
              >
                {me.workspace.feedbackEnabled ? t.feedback.settingOn : t.feedback.settingOff}
              </Button>
              <Link to="/reportes" className="text-sm text-muted hover:text-text">
                {t.feedback.viewReports}
              </Link>
            </div>
          </section>
        ) : null}

        <p className="mb-6 text-xs text-dim">
          {t.version}: {__APP_VERSION__}
        </p>
        <section className="mb-8">
          <SectionTitle>{t.pushTitle}</SectionTitle>
          <p className="mb-3 text-sm text-muted">{t.pushHint}</p>
          {!pushSupported() ? (
            <Alert>{t.pushUnsupported}</Alert>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={pushOn ? "ghost" : "primary"}
                loading={busy === "push"}
                onClick={() =>
                  run(
                    "push",
                    async () => {
                      if (pushOn) {
                        await disablePush();
                        setPushOn(false);
                        return;
                      }
                      const r = await enablePush();
                      if (r === "denied") throw new Error("push_denied");
                      if (r !== "ok") throw new Error("push_unavailable");
                      setPushOn(true);
                    },
                    pushOn ? t.pushDisabled : t.pushEnabled,
                  )
                }
              >
                {pushOn ? t.pushTurnOff : t.pushTurnOn}
              </Button>
              {pushOn ? (
                <Button
                  variant="ghost"
                  loading={busy === "pushtest"}
                  onClick={() =>
                    run(
                      "pushtest",
                      async () => void (await api("/api/push/test", { method: "POST" })),
                      t.pushTestSent,
                    )
                  }
                >
                  {t.pushTest}
                </Button>
              ) : null}
            </div>
          )}
        </section>

        <section className="mb-8">
          <SectionTitle>{t.sessions}</SectionTitle>
          <p className="mb-3 text-sm text-muted">{t.sessionsHint}</p>
          <Button variant="ghost" onClick={signOutEverywhere} loading={busy === "sessions"}>
            {t.signOutEverywhere}
          </Button>
        </section>
      </div>
    </Shell>
  );
}
